import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import FilterSelect from './FilterSelect';
import USER_FILTER_TYPE from '../../constants';

// Keeps sub-calendars right under their parent
const buildCalendarOptions = (calendars) => {
  const calendarMap = calendars.reduce((acc, calendar) => {
    acc[calendar.id] = calendar;
    return acc;
  }, {});

  const childrenByParentId = calendars.reduce((acc, calendar) => {
    const { parentCalendarId } = calendar;
    if (parentCalendarId && calendarMap[parentCalendarId]) {
      acc[parentCalendarId] = acc[parentCalendarId] || [];
      acc[parentCalendarId].push(calendar);
    }
    return acc;
  }, {});

  const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

  const toOption = (calendar, level) => ({
    value: `calendar-${calendar.id}`,
    label: calendar.name,
    filterType: USER_FILTER_TYPE.Calendars,
    filterId: calendar.id,
    icon: 'calendar2',
    level,
  });

  const flatten = (calendarList, level) => calendarList
    .slice()
    .sort(byName)
    .flatMap((calendar) => [
      toOption(calendar, level),
      ...flatten(childrenByParentId[calendar.id] || [], level + 1),
    ]);

  // A calendar whose parent is missing is shown at the top level
  const topLevelCalendars = calendars.filter((calendar) => (
    !calendar.parentCalendarId || !calendarMap[calendar.parentCalendarId]
  ));

  return flatten(topLevelCalendars, 0);
};

const ALL_ATHLETES_OPTION = {
  value: 'all-athletes',
  label: 'All Athletes',
  filterType: USER_FILTER_TYPE.AllAthletes,
  filterId: null,
  icon: 'user',
};

const buildOptions = (athletes, groups, calendars) => [
  ALL_ATHLETES_OPTION,
  {
    label: 'Athletes',
    options: athletes.map((athlete) => ({
      value: `athlete-${athlete.id}`,
      label: `${athlete.first} ${athlete.last}`,
      filterType: USER_FILTER_TYPE.Athletes,
      filterId: athlete.id,
      icon: 'user',
    })),
  },
  {
    label: 'Groups',
    options: groups.map((group) => ({
      value: `group-${group.id}`,
      label: group.name,
      filterType: USER_FILTER_TYPE.Groups,
      filterId: group.id,
      icon: 'group',
    })),
  },
  {
    label: 'Calendars',
    options: buildCalendarOptions(calendars),
  },
];

const MultiCategoryFilter = ({
  groups, calendars, athletes, value, onChange, isLoading,
}) => {
  const optionGroups = useMemo(
    () => buildOptions(athletes, groups, calendars),
    [athletes, groups, calendars],
  );

  const selectedOption = useMemo(() => {
    const flatOptions = optionGroups.flatMap((group) => group.options || group);
    return flatOptions.find((opt) => (
      opt.filterType === value.filterType && opt.filterId === (value.filterIds?.[0] ?? null)
    )) || null;
  }, [optionGroups, value]);

  const handleChange = (option) => {
    if (!option) return;
    onChange({
      filterType: option.filterType,
      filterIds: option.filterId === null ? [] : [option.filterId],
    });
  };

  return (
    <FilterSelect
      inputId='maxes-goals-who-to-update'
      ariaLabel='Who to update'
      icon='user'
      options={optionGroups}
      value={selectedOption}
      onChange={handleChange}
      isLoading={isLoading}
      placeholder='Select athlete, group, or calendar'
      searchOverlayText='Search for an athlete, group, or calendar'
    />
  );
};

MultiCategoryFilter.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  calendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    parentCalendarId: PropTypes.number,
  })),
  athletes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
  })),
  value: PropTypes.shape({
    filterType: PropTypes.number,
    filterIds: PropTypes.arrayOf(PropTypes.number),
  }),
  onChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

MultiCategoryFilter.defaultProps = {
  groups: [],
  calendars: [],
  athletes: [],
  value: { filterType: null, filterIds: [] },
  isLoading: false,
};

export default MultiCategoryFilter;
